import { useAxios, useFeatureFlags } from '@/app/composable';
import { Ref, computed, ref } from '@vue/composition-api';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import * as R from 'ramda';
import { StatisticsAPI } from '../api';
import store from '@/app/store';
import { DashboardStatistic } from '../interfaces';

dayjs.extend(utc);

export function useDashboard() {
    const { exec, loading } = useAxios(true);
    const { isEnabled, areAnyEnabled } = useFeatureFlags();

    const user = computed(() => store.state.auth.user);

    const error: Ref<string | undefined> = ref<string | undefined>();
    const lastUpdatedOn: Ref<string | undefined> = ref<string | undefined>();
    const lastUpdatedSince: Ref<string | undefined> = ref<string | undefined>();

    const allAssetStatistics: Ref<DashboardStatistic[]> = computed((): DashboardStatistic[] => {
        const all: DashboardStatistic[] = [
            {
                path: ['assets', 'datasets'],
                title: 'Datasets',
                icon: 'datasets',
                singular: 'dataset',
                plural: 'datasets',
                percent: 0,
                mine: 0,
                organisation: 0,
                link: {
                    overview: { name: 'assets', query: { tab: 'datasets' } },
                    mine: { name: 'assets', query: { tab: 'datasets', user: `${user.value.id}` } },
                    organisation: { name: 'assets', query: { tab: 'datasets' } },
                },
            },
            {
                path: ['assets', 'results'],
                title: 'Results',
                icon: 'results',
                singular: 'result',
                plural: 'results',
                mine: 0,
                organisation: 0,
                percent: 0,
                link: {
                    overview: { name: 'assets', query: { tab: 'results' } },
                    mine: { name: 'assets', query: { tab: 'results', user: `${user.value.id}` } },
                    organisation: { name: 'assets', query: { tab: 'results' } },
                },
                feature: 'analytics',
            },
        ];

        return all.filter(
            (stat: DashboardStatistic) =>
                R.isNil(stat.feature) ||
                (!Array.isArray(stat.feature) && isEnabled(stat.feature)) ||
                (Array.isArray(stat.feature) && areAnyEnabled(stat.feature)),
        );
    });

    const allPipelineAndretrievalStatistics: Ref<DashboardStatistic[]> = computed((): DashboardStatistic[] => {
        const all: DashboardStatistic[] = [
            {
                path: ['pipelines', 'dataCheckin'],
                title: 'Ingestion Jobs',
                icon: 'ingestion',
                singular: 'ingestion job',
                plural: 'ingestion jobs',
                mine: 0,
                organisation: 0,
                percent: 0,
                link: {
                    overview: { name: 'data-checkin-jobs', query: {} },
                    mine: { name: 'data-checkin-jobs', query: {} },
                    organisation: { name: 'data-checkin-jobs', query: { tab: 'shared' } },
                },
            },
            {
                path: ['pipelines', 'dataAnalytics'],
                title: 'Data Analytics Pipelines',
                icon: 'analytics',
                singular: 'analytics pipeline',
                plural: 'analytics pipelines',
                mine: 0,
                organisation: 0,
                percent: 0,
                link: {
                    overview: { name: 'workflows', query: {} },
                    mine: { name: 'workflows', query: {} },
                    organisation: { name: 'workflows', query: { tab: 'shared' } },
                },
                feature: 'analytics',
            },
            {
                path: ['retrievalQueries'],
                title: 'Retrieval Queries',
                icon: 'retrieval',
                singular: 'retrieval query',
                plural: 'retrieval queries',
                mine: 0,
                organisation: 0,
                percent: 0,
                link: {
                    overview: { name: 'retrieval', query: {} },
                    mine: { name: 'retrieval', query: {} },
                },
                feature: ['retrieve.api', 'retrieve.files', 'retrieve.stream'],
            },
        ];
        return all.filter(
            (stat: DashboardStatistic) =>
                R.isNil(stat.feature) ||
                (!Array.isArray(stat.feature) && isEnabled(stat.feature)) ||
                (Array.isArray(stat.feature) && areAnyEnabled(stat.feature)),
        );
    });

    const asset: Ref<DashboardStatistic[]> = ref<DashboardStatistic[]>(allAssetStatistics.value);

    const pipelineAndRetrieval: Ref<DashboardStatistic[]> = ref<DashboardStatistic[]>(
        allPipelineAndretrievalStatistics.value,
    );

    const getPercent = (numerator: number, denomenator: number) =>
        denomenator > 0 ? Math.round((numerator / denomenator + Number.EPSILON) * 100) : 0;

    const fetchStatistics = (forceClearCache = false) => {
        exec(StatisticsAPI.statistics(forceClearCache))
            .then((res: any) => {
                lastUpdatedOn.value = dayjs.utc(res.data.lastUpdatedOn).format('DD/MM/YYYY HH:mm');
                lastUpdatedSince.value = dayjs.utc(res.data.lastUpdatedOn).fromNow();
                asset.value = allAssetStatistics.value.map((stat: DashboardStatistic) => {
                    return {
                        ...stat,
                        ...R.path(stat.path, res.data),
                        percent: getPercent(
                            (R.path(stat.path, res.data) as any).mine,
                            (R.path(stat.path, res.data) as any).organisation,
                        ),
                    };
                });

                pipelineAndRetrieval.value = allPipelineAndretrievalStatistics.value.map((stat: DashboardStatistic) => {
                    return {
                        ...stat,
                        ...R.path(stat.path, res.data),
                        percent: getPercent(
                            (R.path(stat.path, res.data) as any).mine,
                            (R.path(stat.path, res.data) as any).organisation,
                        ),
                    };
                });
            })
            .catch((e) => {
                error.value = e.message;
            });
    };

    const refetch = () => fetchStatistics(true);

    fetchStatistics();
    return {
        asset,
        pipelineAndRetrieval,
        loading,
        lastUpdatedOn,
        lastUpdatedSince,
        refetch,
    };
}
